import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Printer, Copy, Edit3, Share2, EyeOff, Trash2, Sparkles, Globe2, PenSquare, PlusCircle } from "lucide-react";

/**
 * Drill detail + inline edit + coach actions (publish/unpublish/clone/delete).
 * Prints via `window.print()` — a print-friendly stylesheet keeps the body
 * legible on paper. All actions guard by author + admin.
 */

const SOURCE_META = {
  rally_lab: { label: "Rally Lab Official", color: "#CCFF00", icon: Sparkles },
  coach_shared: { label: "Coach-shared", color: "#00BFFF", icon: Globe2 },
  coach_draft: { label: "Draft (only you)", color: "#F59E0B", icon: PenSquare },
};

const SUB_SKILLS = {
  pickleball: ["Serve", "Return", "Dink", "Third Shot Drop", "Volley", "Reset", "Footwork", "Kitchen Play", "Mental", "Fitness"],
  squash: ["Length", "Drives", "Boast", "Drop Shot", "Volley", "Lob", "Footwork", "T-Position", "Mental", "Fitness"],
};

export default function DrillDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isNew = id === "new";

  const [drill, setDrill] = useState(null);
  const [editing, setEditing] = useState(isNew);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Reset editing state whenever we navigate to a different drill (e.g. after
  // a fresh-save redirect from /drills/new → /drills/:id). Without this the
  // form stayed open on the newly-persisted drill.
  useEffect(() => { setEditing(id === "new"); }, [id]);

  useEffect(() => {
    if (isNew) {
      const blank = {
        title: "", sport: "pickleball", summary: "", description: "",
        coaching_cues: [""], sub_skills: [], skill_level_min: 3.0, skill_level_max: 4.0,
        duration_min: 10, court_setup: "", image_urls: [""], video_url: "",
      };
      setForm(blank);
      return;
    }
    api.get(`/drills/${id}`).then((r) => {
      setDrill(r.data);
      setForm({ ...r.data, coaching_cues: r.data.coaching_cues?.length ? r.data.coaching_cues : [""], image_urls: r.data.image_urls?.length ? r.data.image_urls : [""] });
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load drill"));
  }, [id, isNew]);

  const canEdit = drill && (
    (drill.source === "rally_lab" && (user?.permissions || []).includes("super_admin")) ||
    (drill.source !== "rally_lab" && drill.author_id === user?.id) ||
    (user?.permissions || []).includes("admin")
  );

  const save = async () => {
    if (!form?.title?.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      // Strip empty rows before send
      const payload = {
        ...form,
        coaching_cues: (form.coaching_cues || []).filter((s) => s.trim()),
        image_urls: (form.image_urls || []).filter((s) => s.trim()),
        skill_level_min: parseFloat(form.skill_level_min),
        skill_level_max: parseFloat(form.skill_level_max),
        duration_min: parseInt(form.duration_min, 10),
      };
      if (isNew) {
        const r = await api.post("/drills", payload);
        toast.success("Drill created — saved as draft. Publish to share with other coaches.");
        nav(`/drills/${r.data.id}`, { replace: true });
      } else {
        const r = await api.patch(`/drills/${id}`, payload);
        setDrill(r.data);
        setEditing(false);
        toast.success("Drill updated");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const publish = async () => {
    try {
      await api.post(`/drills/${id}/publish`);
      toast.success("Drill is now visible to all coaches");
      const r = await api.get(`/drills/${id}`);
      setDrill(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const unpublish = async () => {
    try {
      await api.post(`/drills/${id}/unpublish`);
      toast.success("Drill is now private again");
      const r = await api.get(`/drills/${id}`);
      setDrill(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const clone = async () => {
    try {
      const r = await api.post(`/drills/${id}/clone`);
      toast.success("Cloned into your drafts — edit the copy freely");
      nav(`/drills/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async () => {
    if (!window.confirm("Delete this drill? Routines that reference it will lose it.")) return;
    try {
      await api.delete(`/drills/${id}`);
      toast.success("Drill deleted");
      nav("/drills");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (isNew && !form) return null;
  if (!isNew && !drill) return <div className="pt-14 p-6 text-sm text-[#A3A3A3]">Loading…</div>;

  const meta = drill && SOURCE_META[drill.source];
  const Icon = meta?.icon;

  // ------- EDIT VIEW -------
  if (editing && form) {
    const updateCue = (i, v) => setForm({ ...form, coaching_cues: form.coaching_cues.map((c, idx) => idx === i ? v : c) });
    const updateImg = (i, v) => setForm({ ...form, image_urls: form.image_urls.map((c, idx) => idx === i ? v : c) });
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white pt-14 pb-16">
        <div className="max-w-3xl mx-auto px-6 space-y-5">
          <Link to="/drills" className="inline-flex items-center gap-1 text-xs text-[#A3A3A3] hover:text-[#CCFF00]"><ArrowLeft className="w-3 h-3" /> Library</Link>
          <h1 className="h-display text-3xl">{isNew ? "New drill" : "Edit drill"}</h1>

          <div className="space-y-4">
            <div>
              <div className="label-mono mb-1 text-[10px]">TITLE *</div>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm" data-testid="drill-title" />
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <div className="label-mono mb-1 text-[10px]">SPORT</div>
                <select value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value, sub_skills: [] })} className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm" data-testid="drill-sport">
                  <option value="pickleball">Pickleball</option>
                  <option value="squash">Squash</option>
                </select>
              </div>
              <div>
                <div className="label-mono mb-1 text-[10px]">DURATION (MIN)</div>
                <input type="number" min="1" max="120" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm" data-testid="drill-duration" />
              </div>
              <div>
                <div className="label-mono mb-1 text-[10px]">SKILL LEVEL</div>
                <div className="flex gap-2">
                  <input type="number" step="0.5" min="1" max="5" value={form.skill_level_min} onChange={(e) => setForm({ ...form, skill_level_min: e.target.value })} className="w-full bg-[#141414] border border-[#262626] px-2 py-2 text-sm" data-testid="drill-level-min" />
                  <input type="number" step="0.5" min="1" max="5" value={form.skill_level_max} onChange={(e) => setForm({ ...form, skill_level_max: e.target.value })} className="w-full bg-[#141414] border border-[#262626] px-2 py-2 text-sm" data-testid="drill-level-max" />
                </div>
              </div>
            </div>
            <div>
              <div className="label-mono mb-1 text-[10px]">SUB-SKILL TAGS</div>
              <div className="flex flex-wrap gap-1.5" data-testid="drill-sub-skills">
                {SUB_SKILLS[form.sport].map((s) => {
                  const selected = form.sub_skills.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setForm({ ...form, sub_skills: selected ? form.sub_skills.filter((x) => x !== s) : [...form.sub_skills, s] })}
                      className="text-[10px] uppercase tracking-wider px-2 py-1 border transition-colors"
                      style={selected ? { background: "#CCFF00", color: "#000", borderColor: "transparent" } : { borderColor: "#262626", color: "#A3A3A3" }}
                      data-testid={`sub-skill-${s.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="label-mono mb-1 text-[10px]">ONE-LINER SUMMARY</div>
              <input value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="Shown in the library list view" className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm" data-testid="drill-summary" />
            </div>
            <div>
              <div className="label-mono mb-1 text-[10px]">DESCRIPTION</div>
              <textarea rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What the drill is, how to run it, targets…" className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm" data-testid="drill-description" />
            </div>
            <div>
              <div className="label-mono mb-1 text-[10px]">COURT SETUP</div>
              <textarea rows={2} value={form.court_setup} onChange={(e) => setForm({ ...form, court_setup: e.target.value })} placeholder="Player positions, cones, equipment…" className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm" data-testid="drill-court-setup" />
            </div>
            <div>
              <div className="label-mono mb-1 text-[10px]">COACHING CUES</div>
              {form.coaching_cues.map((c, i) => (
                <input
                  key={i}
                  value={c}
                  onChange={(e) => updateCue(i, e.target.value)}
                  placeholder={`Cue ${i + 1}`}
                  className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm mb-1"
                  data-testid={`cue-${i}`}
                />
              ))}
              <button type="button" onClick={() => setForm({ ...form, coaching_cues: [...form.coaching_cues, ""] })} className="text-[10px] uppercase tracking-wider text-[#CCFF00] inline-flex items-center gap-1" data-testid="add-cue">
                <PlusCircle className="w-3 h-3" /> Add cue
              </button>
            </div>
            <div>
              <div className="label-mono mb-1 text-[10px]">IMAGE URLS (paste any web-accessible link)</div>
              {form.image_urls.map((u, i) => (
                <input key={i} value={u} onChange={(e) => updateImg(i, e.target.value)} placeholder="https://…/diagram.png" className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm mb-1" data-testid={`image-url-${i}`} />
              ))}
              <button type="button" onClick={() => setForm({ ...form, image_urls: [...form.image_urls, ""] })} className="text-[10px] uppercase tracking-wider text-[#CCFF00] inline-flex items-center gap-1" data-testid="add-image">
                <PlusCircle className="w-3 h-3" /> Add image
              </button>
            </div>
            <div>
              <div className="label-mono mb-1 text-[10px]">VIDEO URL (optional)</div>
              <input value={form.video_url || ""} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="YouTube / Vimeo / mp4 link" className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm" data-testid="drill-video-url" />
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={saving} className="btn-volt px-6 py-2 text-xs disabled:opacity-50" data-testid="drill-save">
              {saving ? "Saving…" : (isNew ? "Create drill" : "Save changes")}
            </button>
            {!isNew && <button onClick={() => setEditing(false)} className="px-4 py-2 text-xs text-[#A3A3A3]">Cancel</button>}
          </div>
        </div>
      </div>
    );
  }

  // ------- READ VIEW -------
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pt-14 pb-16 print:bg-white print:text-black print:pt-2">
      <div className="max-w-3xl mx-auto px-6 space-y-5 print:space-y-3">
        <div className="print:hidden">
          <Link to="/drills" className="inline-flex items-center gap-1 text-xs text-[#A3A3A3] hover:text-[#CCFF00]" data-testid="back-to-library">
            <ArrowLeft className="w-3 h-3" /> Library
          </Link>
        </div>

        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              {meta && (
                <span className="text-[9px] uppercase tracking-wider label-mono px-1.5 py-0.5 print:hidden" style={{ color: meta.color, borderColor: meta.color, borderWidth: 1 }} data-testid="drill-source-badge">
                  <Icon className="w-2.5 h-2.5 inline mr-0.5" /> {meta.label}
                </span>
              )}
              <span className="text-[10px] text-[#A3A3A3] label-mono print:text-black">
                L{drill.skill_level_min}-{drill.skill_level_max} · {drill.duration_min} min · {drill.sport}
              </span>
            </div>
            <h1 className="h-display text-3xl print:text-2xl" data-testid="drill-title-view">{drill.title}</h1>
            {drill.summary && <p className="text-sm text-[#A3A3A3] mt-2 print:text-black">{drill.summary}</p>}
            <div className="flex flex-wrap gap-1 mt-2 print:hidden">
              {(drill.sub_skills || []).map((s) => (
                <span key={s} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#262626] text-[#A3A3A3]">{s}</span>
              ))}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap print:hidden">
            <button onClick={() => window.print()} className="px-3 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-1.5" data-testid="print-drill">
              <Printer className="w-3.5 h-3.5" /> Print / PDF
            </button>
            <button onClick={clone} className="px-3 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-1.5" data-testid="clone-drill">
              <Copy className="w-3.5 h-3.5" /> Clone
            </button>
            {canEdit && (
              <button onClick={() => setEditing(true)} className="px-3 py-2 text-xs uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] inline-flex items-center gap-1.5" data-testid="edit-drill">
                <Edit3 className="w-3.5 h-3.5" /> Edit
              </button>
            )}
            {canEdit && drill.source === "coach_draft" && (
              <button onClick={publish} className="px-3 py-2 text-xs uppercase tracking-wider bg-[#00BFFF] text-black inline-flex items-center gap-1.5" data-testid="publish-drill">
                <Share2 className="w-3.5 h-3.5" /> Share
              </button>
            )}
            {canEdit && drill.source === "coach_shared" && (
              <button onClick={unpublish} className="px-3 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#A3A3A3] inline-flex items-center gap-1.5" data-testid="unpublish-drill">
                <EyeOff className="w-3.5 h-3.5" /> Unshare
              </button>
            )}
            {canEdit && drill.source !== "rally_lab" && (
              <button onClick={del} className="px-3 py-2 text-xs uppercase tracking-wider text-[#FF4500] hover:opacity-70 inline-flex items-center gap-1.5" data-testid="delete-drill">
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            )}
          </div>
        </div>

        {drill.image_urls?.length > 0 && (
          <div className="grid md:grid-cols-2 gap-3">
            {drill.image_urls.map((u, i) => (
              <img key={i} src={u} alt="" className="w-full border border-[#262626] print:border-black" data-testid={`drill-image-${i}`} />
            ))}
          </div>
        )}

        {drill.description && (
          <section>
            <div className="label-mono text-[10px] text-[#CCFF00] mb-2 print:text-black">HOW TO RUN IT</div>
            <p className="text-sm leading-relaxed whitespace-pre-line print:text-black" data-testid="drill-description-view">{drill.description}</p>
          </section>
        )}

        {drill.court_setup && (
          <section>
            <div className="label-mono text-[10px] text-[#CCFF00] mb-2 print:text-black">COURT SETUP</div>
            <p className="text-sm text-[#E5E5E5] print:text-black" data-testid="drill-court-setup-view">{drill.court_setup}</p>
          </section>
        )}

        {drill.coaching_cues?.length > 0 && (
          <section>
            <div className="label-mono text-[10px] text-[#CCFF00] mb-2 print:text-black">COACHING CUES</div>
            <ul className="text-sm space-y-1.5 pl-4 list-disc print:text-black" data-testid="drill-cues-view">
              {drill.coaching_cues.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </section>
        )}

        {drill.video_url && (
          <section className="print:hidden">
            <div className="label-mono text-[10px] text-[#CCFF00] mb-2">VIDEO REFERENCE</div>
            <a href={drill.video_url} target="_blank" rel="noreferrer" className="text-sm text-[#00BFFF] underline break-all">{drill.video_url}</a>
          </section>
        )}

        <div className="text-[10px] text-[#A3A3A3] label-mono pt-4 border-t border-[#262626] print:text-black print:border-black">
          Authored by {drill.author_name || "unknown"} · Last updated {(drill.updated_at || drill.created_at || "").slice(0, 10)}
        </div>
      </div>
    </div>
  );
}
